import axios, { type AxiosInstance } from "axios";
import { HTTP_CONFIG, EXCLUDED_ENDPOINTS } from "../constants";

// Track if auth interceptor is initialized to prevent race conditions
let isInterceptorReady = false;
let interceptorReadyResolvers: Array<() => void> = [];

/**
 * Mark the auth interceptor as ready
 * Called by initAxiosAuthInterceptors after setup completes
 */
export const setInterceptorReady = (): void => {
  isInterceptorReady = true;
  // Resolve any pending requests that were waiting
  interceptorReadyResolvers.forEach(resolve => resolve());
  interceptorReadyResolvers = [];
};

/**
 * Check if auth interceptor is ready
 */
export const getInterceptorReady = (): boolean => isInterceptorReady;

/**
 * Reset interceptor ready state (for testing/cleanup)
 */
export const resetInterceptorReady = (): void => {
  isInterceptorReady = false;
  interceptorReadyResolvers = [];
};

/**
 * Check if URL is an excluded endpoint (no JWT required)
 * @param url - Request URL
 */
const isExcludedUrl = (url?: string): boolean => {
  if (!url) return false;
  if (!url.includes("/api/v1")) return true;
  const pathname = url.split(/[?#]/)[0];
  return EXCLUDED_ENDPOINTS.some(endpoint => pathname.endsWith(endpoint));
};

const created = typeof axios.create === "function" 
  ? axios.create({
      timeout: HTTP_CONFIG.REQUEST_TIMEOUT_MS,
    }) 
  : undefined;

const fallback: any = axios as unknown;
if (!created && (!fallback?.interceptors?.request || !fallback?.interceptors?.response)) {
	fallback.interceptors = {
		request: { use: () => 0, eject: () => {} },
		response: { use: () => 0, eject: () => {} },
	};
}

export const apiClient: AxiosInstance = (created ?? fallback) as AxiosInstance;

// Guard interceptor - wait for auth interceptor to be ready before protected requests
apiClient.interceptors.request.use(async (config) => {
	const url = config.url ?? "";
	
	// Skip waiting for excluded endpoints
	if (isExcludedUrl(url)) {
		return config;
	}
	
	// Wait for interceptor to be ready (max 5 seconds)
	if (!isInterceptorReady) {
		console.warn("Auth interceptor not ready, waiting...", url);
		try {
			await new Promise<void>((resolve, reject) => {
				// Check if already ready
				if (isInterceptorReady) {
					resolve();
					return;
				}
				
				const timeout = setTimeout(() => {
					reject(new Error("Auth interceptor initialization timeout - request blocked"));
				}, 5000);
				
				interceptorReadyResolvers.push(() => {
					clearTimeout(timeout);
					resolve();
				});
			});
			console.log("Auth interceptor ready, proceeding with request", url);
		} catch (error) {
			console.error("Request blocked:", error);
			throw error;
		}
	}
	
	return config;
});

// Default headers (do not force Content-Type for FormData uploads)
apiClient.interceptors.request.use((config) => {
	const headers: any = (config as any).headers ?? {};
	(config as any).headers = headers;

	const hasContentType = Object.keys(headers).some(
		(k) => k.toLowerCase() === "content-type"
	);
	const hasAccept = Object.keys(headers).some((k) => k.toLowerCase() === "accept");

	if (!hasAccept) {
		headers["Accept"] = "application/json";
	}

	const data: any = (config as any).data;
	const isFormData =
		typeof FormData !== "undefined" && data != null && data instanceof FormData;

	// Only set JSON content-type when sending a body and it's not FormData
	if (!hasContentType && data != null && !isFormData) {
		headers["Content-Type"] = "application/json";
	}

	return config;
});

export default apiClient;
